import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlbums } from '../contexts/AlbumContext';
import QRCodeGenerator from '../components/QRCodeGenerator';
import PhotoGrid from '../components/PhotoGrid';
import ShareModal from '../components/ShareModal';
import PhotoUploader from '../components/PhotoUploader';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const Album = () => {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { getAlbum, addPhotoToAlbum } = useAlbums();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAlbumData = async () => {
    try {
      console.log('Fetching album with ID:', id);
      const albumData = await getAlbum(id);
      if (!albumData) {
        console.error('Album not found');
        navigate('/dashboard');
        return;
      }

      if (albumData.ownerId !== user.id) {
        console.error('User does not own this album');
        navigate('/dashboard');
        return;
      }

      setAlbum(albumData);
    } catch (error) {
      console.error('Error fetching album:', error);
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    // Scroll to top when album loads
    window.scrollTo(0, 0);
    
    // Wait for auth to load first
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    const loadAlbum = async () => {
      await fetchAlbumData();
      setLoading(false);
    };

    loadAlbum();
  }, [id, user, navigate, getAlbum, authLoading]);

  const handlePhotoUpload = async (files) => {
    if (!album || !user) return;

    setUploading(true);
    try {
      console.log('Starting private album upload for files:', files);
      console.log('Album data for upload:', {
        id: album.id,
        shareId: album.shareId,
        ownerId: album.ownerId,
        userIsOwner: album.ownerId === user.id
      });

      let successCount = 0;
      let skippedCount = 0;

      for (const file of files) {
        console.log('Processing private album file:', file.name, file.size, file.type);
        const photoData = {
          file: file,
          uploaderName: user.name
        };

        // Use the shareId for consistency with the working public upload
        console.log('Using shareId for private upload (same as public):', album.shareId);
        const result = await addPhotoToAlbum(album.shareId, photoData);

        if (result.success) {
          console.log('Successfully uploaded to private album:', file.name);
          successCount++;
        } else {
          console.log('Skipped file:', file.name, result.error);
          skippedCount++;
          // Continue with next file without showing error popup
        }
      }

      // Only log success if any files were uploaded
      if (successCount > 0) {
        console.log(`Private upload complete: ${successCount} uploaded, ${skippedCount} skipped`);
      }

      // Refresh album data
      await fetchAlbumData();
    } catch (error) {
      console.error('Private upload process failed:', error);
      // Don't show error popup, just log it
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDeleted = async () => {
    // Refresh album data when a photo is deleted
    await fetchAlbumData();
  };

  const shareUrl = album ? `${window.location.origin}/#/share/${album.shareId}` : '';

  // Show loading while auth or album is loading
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-light">Loading album...</p>
        </div>
      </div>
    );
  }

  if (!album) return null;

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Album Header */}
          <div className="border-b border-gray-200 pb-8 mb-12">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-4xl font-light text-black mb-3 tracking-tight">{album.title}</h1>
                {album.description && (
                  <p className="text-gray-600 mb-4 font-light leading-relaxed max-w-2xl">{album.description}</p>
                )}
                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                  <span>{album.photos.length} photos</span>
                  <span>Created {format(new Date(album.createdAt), 'MMMM d, yyyy')}</span>
                  {album.eventDate && (
                    <span>Event: {format(new Date(album.eventDate), 'MMMM d, yyyy')}</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                <button
                  onClick={() => setShowQRCode(true)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-all btn-secondary"
                >
                  QR Code
                </button>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 font-medium transition-all btn-primary"
                >
                  Share Album
                </button>
              </div>
            </div>
          </div>

          {/* Photo Uploader */}
          <div className="mb-12">
            <PhotoUploader
              onUpload={handlePhotoUpload}
              uploading={uploading}
              albumTitle={album.title}
            />
          </div>

          {/* Photo Grid */}
          <PhotoGrid
            photos={album.photos}
            albumTitle={album.title}
            album={album}
            onPhotoDeleted={handlePhotoDeleted}
          />

          {album.photos.length === 0 && (
            <div className="text-center py-20 border border-gray-200 rounded-lg">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <div className="grid grid-cols-2 gap-1 w-8 h-8">
                  <div className="bg-gray-400 rounded-sm"></div>
                  <div className="bg-gray-400 rounded-sm"></div>
                  <div className="bg-gray-400 rounded-sm"></div>
                  <div className="bg-gray-400 rounded-sm"></div>
                </div>
              </div>
              <h3 className="text-xl font-light text-black mb-3">No photos yet</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto font-light">
                Upload photos above or share your album link with guests so they can start contributing photos from your event.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowShareModal(true)}
                  className="bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 font-medium transition-all btn-primary"
                >
                  Share Album
                </button>
                <button
                  onClick={() => setShowQRCode(true)}
                  className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-50 font-medium transition-all btn-secondary"
                >
                  Show QR Code
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {showShareModal && (
        <ShareModal
          shareUrl={shareUrl}
          albumTitle={album.title}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showQRCode && (
        <QRCodeGenerator
          url={shareUrl}
          albumTitle={album.title}
          onClose={() => setShowQRCode(false)}
        />
      )}
    </div>
  );
};

export default Album;